// Aktivitātes kārts spēļu kāršu stilā. Visi izmēri ir relatīvi pret kārts platumu (cqw).

import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import {
  RAR, cardColor, cardKindText, cardOutcome, cardTypeName, mixTo, ruleText, sgn2, sgnR, stackText, type Card, type EnergyNow, type Index,
  type PlayCounter
} from '@balansa/shared';
import { imageUrl } from '../lib/api';
import { BoltSm } from '../lib/icons';

export type CardLike = Omit<Card, 'id' | 'sortOrder'> & { id?: string };

export function GameCard(p: {
  card: CardLike;
  idx: Index;
  en?: EnergyNow | null;
  live?: boolean;
  eager?: boolean;
  number: number;
  imgSrc?: string | null;
  /** Iepriekšējās izspēles (krāšanās bonusu progresam). */
  prior?: PlayCounter;
}) {
  const c = p.card;
  const idx = p.idx;
  const col = cardColor(c, idx);
  const o = p.live && p.en ? cardOutcome(c as Card, idx, p.en, p.prior) : null;
  const stacks = c.stacks ?? [];
  const src = p.imgSrc !== undefined ? p.imgSrc : imageUrl(c.image);
  const [failed, setFailed] = useState<string | null>(null);
  const noimg = !src || failed === src;
  const textRef = useRef<HTMLDivElement>(null);
  const style = { '--tc': col, '--tcl': mixTo(col, true, 0.35), '--tcd': mixTo(col, false, 0.38) } as CSSProperties;
  const fx = c.effects.filter((e) => idx.node.has(e.target));

  // Ja teksts neietilpst, samazina burtus un, ja vajag, paslēpj aprakstu.
  const sig = JSON.stringify([c.name, c.flavor, c.effects, c.rules, stacks, o?.active, o?.stacks]);
  useLayoutEffect(() => {
    const t = textRef.current;
    if (!t) return;
    const fit = () => {
      t.classList.remove('f1', 'f2');
      if (!t.clientHeight) return;
      if (t.scrollHeight > t.clientHeight + 1) {
        t.classList.add('f1');
        if (t.scrollHeight > t.clientHeight + 1) {
          t.classList.remove('f1');
          t.classList.add('f2');
        }
      }
    };
    fit();
    const h = requestAnimationFrame(fit);
    return () => cancelAnimationFrame(h);
  }, [sig]);

  return (
    <article className={'gcard r-' + c.rarity} style={style} data-testid="game-card">
      <div className="gc-frame">
        <div className="gc-in">
          <header className="gc-head">
            <span className="gc-name">{c.name || 'Bez nosaukuma'}</span>
            {c.energy ? (
              <span className={'gc-cost ' + (c.energy > 0 ? 'pos' : 'neg')} title="Enerģija">
                {sgnR(c.energy)}
                <BoltSm />
              </span>
            ) : null}
          </header>
          <div className={'gc-art' + (noimg ? ' noimg' : '')}>
            {src ? <img src={src} alt="" loading={p.eager ? undefined : 'lazy'} onError={() => setFailed(src)} /> : null}
            <div className="fb">
              <span>{(c.name || '?').trim().charAt(0).toUpperCase()}</span>
              <small>{c.image ? 'attēls nav pieejams' : 'nav attēla'}</small>
            </div>
          </div>
          <div className="gc-type">
            <span>Aktivitāte — {cardTypeName(c, idx)}</span>
            <span className="gc-rar" title={RAR[c.rarity][0]}>
              {RAR[c.rarity][1]}
            </span>
          </div>
          <div className="gc-text" ref={textRef}>
            {fx.length ? (
              <ul className="gc-fx">
                {fx.map((e, i) => (
                  <li key={i} className={e.delta >= 0 ? 'up' : 'down'}>
                    <b>{sgn2(e.delta)}</b>
                    <span>{idx.node.get(e.target)!.name}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {c.rules.length ? (
              <ul className="gc-rules">
                {c.rules.map((r, i) => (
                  <li key={i} data-rule={i} className={o && o.active.includes(i) ? 'on' : undefined}>
                    {ruleText(r, idx)}
                  </li>
                ))}
              </ul>
            ) : null}
            {stacks.length ? (
              <ul className="gc-stacks">
                {stacks.map((s, i) => {
                  const st = o?.stacks[i];
                  return (
                    <li key={i} data-stack={i} className={st?.active ? 'on' : undefined}>
                      {stackText(s, idx)}
                      {st && !st.active ? (
                        <span className="gc-prog" title={`Izspēlēta ${st.have - 1}× pēdējās ${s.days} dienās`}>
                          {st.have - 1}/{s.count}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {c.flavor ? <p className="gc-flavor">“{c.flavor}”</p> : null}
          </div>
          <footer className="gc-foot">
            <span>
              #{String(p.number).padStart(3, '0')} · {RAR[c.rarity][0]}
            </span>
            <span className="gc-stat">{cardKindText(c)}</span>
          </footer>
        </div>
      </div>
    </article>
  );
}
